import { Static, TLiteral, Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { ReloadProduct, Subcription } from './namespace';
import { validateEnumTranslations } from '../safety_checks';
import {
  SEO_ACCELERATOR_LINKS_PER_MONTH,
  SEO_ACCELERATOR_MONTHS,
  SEO_ARTICLES_DOUBLE_MONTHLY_PRICE_MINOR,
  SEO_BACKLINK_ACCELERATOR_PRICE_MINOR,
  SEO_EXTRA_BACKLINK_MAX_QUANTITY,
  SEO_EXTRA_BACKLINK_MONTHLY_PRICE_MINOR,
} from './subscription-seo-pack.constants';

// Quotas et prix du pack référencement : définis sans import dans
// `subscription-seo-pack.constants.ts` (chargeable par le générateur du centre d'aide), ré-exportés
// ici pour les consommateurs de la grille.
export {
  SEO_ACCELERATOR_LINKS_PER_MONTH,
  SEO_ACCELERATOR_MONTHS,
  SEO_ARTICLES_DOUBLE_MONTHLY_PRICE_MINOR,
  SEO_BACKLINK_ACCELERATOR_PRICE_MINOR,
  SEO_EXTRA_BACKLINK_MAX_QUANTITY,
  SEO_EXTRA_BACKLINK_MONTHLY_PRICE_MINOR,
  SEO_MAX_BACKLINKS_PER_MONTH,
  SEO_PACK_BACKLINKS_PER_MONTH,
} from './subscription-seo-pack.constants';

export const SUBSCRIPTION_KEYS = Object.keys(Subcription) as Array<keyof typeof Subcription>;
export type SubscriptionKey = keyof typeof Subcription;

export const SUBSCRIPTION_LABEL_TRANSLATE_KEY = 'SUBSCRIPTION_LABEL_';
export const SUBSCRIPTION_DESCRIPTION_TRANSLATE_KEY = 'SUBSCRIPTION_DESCRIPTION_';

// Labels (Starter / Pro / Max). Source de vérité unique pour les noms de formules.
export const SubscriptionLabelTranslation = {
  'fr-FR': {
    [`${SUBSCRIPTION_LABEL_TRANSLATE_KEY}${Subcription.STARTER}`]: 'Starter',
    [`${SUBSCRIPTION_LABEL_TRANSLATE_KEY}${Subcription.PRO}`]: 'Pro',
    [`${SUBSCRIPTION_LABEL_TRANSLATE_KEY}${Subcription.MAX}`]: 'Max',
  },
};

export const SubscriptionDescriptionTranslation = {
  'fr-FR': {
    [`${SUBSCRIPTION_DESCRIPTION_TRANSLATE_KEY}${Subcription.STARTER}`]:
      'Tout pour digitaliser ta prise de rendez-vous : réservations en ligne tous canaux, site internet, fiche Google synchronisée et fiches clients au même endroit.',
    [`${SUBSCRIPTION_DESCRIPTION_TRANSLATE_KEY}${Subcription.PRO}`]:
      'Passe à la vitesse supérieure : paiement en ligne, cartes cadeaux, campagnes email & SMS et plus de quotas pour fidéliser ta clientèle.',
    [`${SUBSCRIPTION_DESCRIPTION_TRANSLATE_KEY}${Subcription.MAX}`]:
      'L’arsenal complet pour scaler : composants premium, API & MCP, paiements avancés, boost SEO et accompagnement Pulse illimité.',
  },
};

export enum UnsubscribeReason {
  TOO_EXPENSIVE = 'TOO_EXPENSIVE',
  NOT_ENOUGH_USAGE = 'NOT_ENOUGH_USAGE',
  FOUND_ALTERNATIVE = 'FOUND_ALTERNATIVE',
  NOT_SATISFIED_QUALITY = 'NOT_SATISFIED_QUALITY',
  TECHNICAL_ISSUES = 'TECHNICAL_ISSUES',
  DOES_NOT_MATCH_NEEDS = 'DOES_NOT_MATCH_NEEDS',
  PERSONAL_REASONS = 'PERSONAL_REASONS',
  BAD_CUSTOMER_SUPPORT = 'BAD_CUSTOMER_SUPPORT',
}

export const UnsubscribeReasonTranslation = {
  'fr-FR': {
    [`${UnsubscribeReason.TOO_EXPENSIVE}`]: 'Je trouve le service trop cher',
    [`${UnsubscribeReason.NOT_ENOUGH_USAGE}`]: "Je n'utilise pas suffisamment le service",
    [`${UnsubscribeReason.FOUND_ALTERNATIVE}`]: "J'ai trouvé une alternative",
    [`${UnsubscribeReason.NOT_SATISFIED_QUALITY}`]: 'Je ne suis pas satisfait(e) de la qualité du service',
    [`${UnsubscribeReason.TECHNICAL_ISSUES}`]: "Problèmes techniques ou difficultés d'utilisation",
    [`${UnsubscribeReason.DOES_NOT_MATCH_NEEDS}`]: "L'offre ne correspond plus à mes besoins",
    [`${UnsubscribeReason.PERSONAL_REASONS}`]: 'Raisons personnelles (déménagement, changement de situation, etc.)',
    [`${UnsubscribeReason.BAD_CUSTOMER_SUPPORT}`]: 'Mauvaise expérience avec le support client',
  },
};

export enum UnsubscribeOfferReason {
  '20_PERCENT_3_MONTHS' = '20_PERCENT_3_MONTHS',
  '50_PERCENT_3_MONTHS' = '50_PERCENT_3_MONTHS',
}

export const UNSUB_OFFER_TRANSLATE_KEY = 'UNSUB_OFFER_';

export const UnsubscribeOfferReasonTranslation = {
  'fr-FR': {
    [`${UNSUB_OFFER_TRANSLATE_KEY}${UnsubscribeOfferReason['20_PERCENT_3_MONTHS']}`]: '20% de réduction pendant 3 mois',
    [`${UNSUB_OFFER_TRANSLATE_KEY}${UnsubscribeOfferReason['50_PERCENT_3_MONTHS']}`]: '50% de réduction pendant 3 mois',
  },
};

export const UNSUB_OFFER_TEXT_TRANSLATE_KEY = 'UNSUB_OFFER_TEXT_';
export const UnsubscribeOfferReasonTextTranslation = {
  'fr-FR': {
    [`${UNSUB_OFFER_TEXT_TRANSLATE_KEY}${UnsubscribeOfferReason['20_PERCENT_3_MONTHS']}`]:
      "Avant de partir, on t'offre 20% de réduction pendant 3 mois pour (re)découvrir le service.",
    [`${UNSUB_OFFER_TEXT_TRANSLATE_KEY}${UnsubscribeOfferReason['50_PERCENT_3_MONTHS']}`]:
      "Avant de partir, on t'offre 50% de réduction pendant 3 mois pour (re)découvrir le service.",
  },
};

export interface SubscriptionPlanList {
  label: Subcription;
  monthly_sms: number;
  monthly_email: number;
  monthly_price_tax_free: number;
  yearly_price_tax_free: number;
}

// Legacy v1 shape — conservé pour la compatibilité de `pro_subscription.service.ts`.
// Quotas SMS/email mis à jour aux valeurs v2 SOLO. Sera supprimé une fois le service migré sur PLANS.
export const LIST_SUBSCRIPTION: SubscriptionPlanList[] = [
  {
    label: Subcription.STARTER,
    monthly_sms: 50,
    monthly_email: 1_000,
    monthly_price_tax_free: 3900,
    yearly_price_tax_free: 2900,
  },
  {
    label: Subcription.PRO,
    monthly_sms: 300,
    monthly_email: 5_000,
    monthly_price_tax_free: 6500,
    yearly_price_tax_free: 4900,
  },
  {
    label: Subcription.MAX,
    monthly_sms: 800,
    monthly_email: 15_000,
    monthly_price_tax_free: 9900,
    yearly_price_tax_free: 7900,
  },
];

// =============================================================================
// Pricing v2 — config-first
// =============================================================================
// Tout ce qui suit est la source de vérité v2 : plans, add-ons, top-ups,
// helpers de quota et de prix. La DB ne stocke que l'état tenant ; ces objets
// décrivent la grille tarifaire et sont consommés par backend + frontend.
// Voir docs/pricing-v2-spec.md pour le modèle complet.
// =============================================================================

export type PlanId = 'starter' | 'pro' | 'max';
export type PlanSize = 'solo' | 'team';
export type Commitment = 'committed_12m' | 'monthly';

export const PLAN_ID_BY_ENUM: Record<Subcription, PlanId> = {
  [Subcription.STARTER]: 'starter',
  [Subcription.PRO]: 'pro',
  [Subcription.MAX]: 'max',
};

/**
 * Rang d'un couple (plan, size). Source de vérité pour direction upgrade/downgrade :
 * solo→team du même plan compte comme upgrade (rank +1). Évite la logique implicite
 * "même plan + bonus team" éparpillée dans les services.
 *
 * Convention : 100 par palier de plan, +1 pour team (loin de tout chevauchement).
 */
export const PLAN_RANK_TABLE: Record<`${Subcription}_${PlanSize}`, number> = {
  [`${Subcription.STARTER}_solo`]: 100,
  [`${Subcription.STARTER}_team`]: 101,
  [`${Subcription.PRO}_solo`]: 200,
  [`${Subcription.PRO}_team`]: 201,
  [`${Subcription.MAX}_solo`]: 300,
  [`${Subcription.MAX}_team`]: 301,
};

export function getPlanRank(label: Subcription, size: PlanSize): number {
  return PLAN_RANK_TABLE[`${label}_${size}`];
}

export const ENUM_BY_PLAN_ID: Record<PlanId, Subcription> = {
  starter: Subcription.STARTER,
  pro: Subcription.PRO,
  max: Subcription.MAX,
};

export interface PlanPriceVariant {
  committed_minor: number;
  monthly_minor: number;
}

export interface PlanQuotas {
  sms: number;
  emails: number;
  gmb_connected: number;
  pro_email_per_user: number;
  pulse_chat_per_user: number | 'unlimited';
}

export interface PlanDefinition {
  id: PlanId;
  enum_value: Subcription;
  label: string;
  tagline_translation_key: string;
  featured: boolean;
  prices: {
    solo: PlanPriceVariant;
    team: PlanPriceVariant & { included_users: number };
  };
  quotas: PlanQuotas;
  capabilities: readonly string[];
}

export const CAPABILITIES = {
  BOOKING_FROM_SITE_AND_GMB: 'booking.from_site_and_gmb',
  PLACES_UNLIMITED: 'places.unlimited',
  SITE_BUILDER: 'site.builder',
  SITE_FR_DOMAIN_INCLUDED: 'site.fr_domain_included',
  SITE_BUILDER_PREMIUM_COMPONENTS: 'site.builder_premium_components',
  SITE_DNS_AND_SCRIPTS: 'site.dns_and_scripts',
  // Tracking analytics par IDs (GTM/GA4/Meta Pixel) — dispo dès PRO, contrairement à
  // SITE_DNS_AND_SCRIPTS (zone DNS + scripts custom) qui reste MAX. Cette capability
  // sert la grille marketing ; le gating runtime est FEATURES.TRACKING (website.tracking).
  SITE_ANALYTICS_TRACKING: 'site.analytics_tracking',
  MARKETING_GOOGLE_REVIEWS_AUTO: 'marketing.google_reviews_auto',
  MARKETING_AI_REVIEW_REPLY: 'marketing.ai_review_reply',
  MARKETING_EMAIL_SMS_CAMPAIGNS: 'marketing.email_sms_campaigns',
  PAYMENTS_ONLINE_AT_BOOKING: 'payments.online_at_booking',
  PAYMENTS_GIFT_CARDS: 'payments.gift_cards',
  PAYMENTS_PAYMENT_LINKS: 'payments.payment_links',
  PAYMENTS_CB_IMPRINT: 'payments.cb_imprint',
  PAYMENTS_INSTALLMENTS: 'payments.installments',
  DEV_API_AND_WEBHOOKS: 'dev.api_and_webhooks',
  SEO_BOOST: 'seo.boost',
  // Statistiques d'activité — grille marketing seulement ; le gating runtime est
  // FEATURES.ADVANCED_STATS (scope stats_advanced, PRO) et FEATURES.TEAM_STATS (scope
  // stats_team, MAX), cf. SITE_ANALYTICS_TRACKING pour le même découplage.
  ADVANCED_STATS: 'stats.advanced',
  TEAM_STATS: 'stats.team',
} as const;

export const PLANS: Record<PlanId, PlanDefinition> = {
  starter: {
    id: 'starter',
    enum_value: Subcription.STARTER,
    label: 'Starter',
    tagline_translation_key: 'PLAN_STARTER_TAGLINE',
    featured: false,
    prices: {
      solo: { committed_minor: 2900, monthly_minor: 3900 },
      team: { committed_minor: 4900, monthly_minor: 6500, included_users: 3 },
    },
    quotas: {
      sms: 50,
      emails: 1_000,
      gmb_connected: 1,
      pro_email_per_user: 0,
      pulse_chat_per_user: 100,
    },
    capabilities: [
      CAPABILITIES.BOOKING_FROM_SITE_AND_GMB,
      CAPABILITIES.PLACES_UNLIMITED,
      CAPABILITIES.SITE_BUILDER,
      CAPABILITIES.SITE_FR_DOMAIN_INCLUDED,
      CAPABILITIES.MARKETING_GOOGLE_REVIEWS_AUTO,
    ],
  },
  pro: {
    id: 'pro',
    enum_value: Subcription.PRO,
    label: 'Pro',
    tagline_translation_key: 'PLAN_PRO_TAGLINE',
    featured: true,
    prices: {
      solo: { committed_minor: 4900, monthly_minor: 6500 },
      team: { committed_minor: 7900, monthly_minor: 9900, included_users: 5 },
    },
    quotas: {
      sms: 300,
      emails: 5_000,
      gmb_connected: 2,
      pro_email_per_user: 1,
      pulse_chat_per_user: 1_000,
    },
    capabilities: [
      CAPABILITIES.BOOKING_FROM_SITE_AND_GMB,
      CAPABILITIES.PLACES_UNLIMITED,
      CAPABILITIES.SITE_BUILDER,
      CAPABILITIES.SITE_FR_DOMAIN_INCLUDED,
      CAPABILITIES.SITE_BUILDER_PREMIUM_COMPONENTS,
      CAPABILITIES.SITE_ANALYTICS_TRACKING,
      CAPABILITIES.MARKETING_GOOGLE_REVIEWS_AUTO,
      CAPABILITIES.MARKETING_EMAIL_SMS_CAMPAIGNS,
      CAPABILITIES.PAYMENTS_ONLINE_AT_BOOKING,
      CAPABILITIES.PAYMENTS_GIFT_CARDS,
      CAPABILITIES.PAYMENTS_PAYMENT_LINKS,
      CAPABILITIES.ADVANCED_STATS,
    ],
  },
  max: {
    id: 'max',
    enum_value: Subcription.MAX,
    label: 'Max',
    tagline_translation_key: 'PLAN_MAX_TAGLINE',
    featured: false,
    prices: {
      solo: { committed_minor: 7900, monthly_minor: 9900 },
      team: { committed_minor: 12900, monthly_minor: 15900, included_users: 10 },
    },
    quotas: {
      sms: 800,
      emails: 15_000,
      gmb_connected: 5,
      pro_email_per_user: 2,
      pulse_chat_per_user: 'unlimited',
    },
    capabilities: [
      CAPABILITIES.BOOKING_FROM_SITE_AND_GMB,
      CAPABILITIES.PLACES_UNLIMITED,
      CAPABILITIES.SITE_BUILDER,
      CAPABILITIES.SITE_FR_DOMAIN_INCLUDED,
      CAPABILITIES.SITE_BUILDER_PREMIUM_COMPONENTS,
      CAPABILITIES.SITE_ANALYTICS_TRACKING,
      CAPABILITIES.SITE_DNS_AND_SCRIPTS,
      CAPABILITIES.MARKETING_GOOGLE_REVIEWS_AUTO,
      CAPABILITIES.MARKETING_AI_REVIEW_REPLY,
      CAPABILITIES.MARKETING_EMAIL_SMS_CAMPAIGNS,
      CAPABILITIES.PAYMENTS_ONLINE_AT_BOOKING,
      CAPABILITIES.PAYMENTS_GIFT_CARDS,
      CAPABILITIES.PAYMENTS_PAYMENT_LINKS,
      CAPABILITIES.PAYMENTS_CB_IMPRINT,
      CAPABILITIES.PAYMENTS_INSTALLMENTS,
      CAPABILITIES.DEV_API_AND_WEBHOOKS,
      CAPABILITIES.SEO_BOOST,
      CAPABILITIES.ADVANCED_STATS,
      CAPABILITIES.TEAM_STATS,
    ],
  },
};

// Soft-cap interne anti-abus pour Max (non communiqué — la promesse externe est "illimité")
export const PULSE_CHAT_MAX_SOFT_CAP_PER_USER = 8_000;

export const WELCOME_BOOST_DURATION_DAYS = 30;

// Add-ons quantitatifs (rangés en `pro_subscription_addon`). Les deux options du pack
// référencement y sont aussi (`seo_articles_double` à quantité fixe 1) : la ligne porte
// `active_since` / `active_until`, ce dont le cycle mensuel a besoin pour « actif au 1er ».
export type QuantityAddonCode =
  | 'extra_user'
  | 'extra_gmb'
  | 'extra_domain'
  | 'seo_extra_backlink'
  | 'seo_articles_double';

// Add-ons binaires (flags directs sur `pro_subscription`)
export type FlagAddonCode = 'white_label' | 'smart_ride' | 'mobile_app_whitelabel';

export type AddonCode = QuantityAddonCode | FlagAddonCode;

/**
 * Mode de facturation d'une option (docs/to_implement/seo/seo-upsell-offers.md, U9) :
 *  - `prorata` (défaut) : effet immédiat, prorata du segment courant débité à l'activation ;
 *  - `next_month` : effet au 1er du mois suivant, jamais de prorata ni de débit immédiat, la
 *    ligne au prix plein apparaît sur la prochaine échéance (décalage d'au plus un mois accepté).
 */
export type AddonBilling = 'prorata' | 'next_month';

export interface AddonDefinition {
  code: AddonCode;
  storage: 'quantity_row' | 'flag';
  monthly_price_minor?: number;
  yearly_price_range_minor?: [number, number];
  is_eligible: (ctx: { plan_id: PlanId; size: PlanSize }) => boolean;
  available_after?: string;
  /** Absent = `prorata`. */
  billing?: AddonBilling;
  /** `false` = jamais proposée au checkout de première souscription (U15). Absent = proposée. */
  checkout?: boolean;
  /** Quantité maximale d'une option « quantité » (absent = 100, le plafond du schéma). */
  max_quantity?: number;
}

export function getAddonBilling(def: Pick<AddonDefinition, 'billing'>): AddonBilling {
  return def.billing ?? 'prorata';
}

export const ADDONS: Record<AddonCode, AddonDefinition> = {
  extra_user: {
    code: 'extra_user',
    storage: 'quantity_row',
    monthly_price_minor: 700,
    is_eligible: (ctx): boolean => ctx.plan_id === 'max' && ctx.size === 'team',
  },
  extra_gmb: {
    code: 'extra_gmb',
    storage: 'quantity_row',
    monthly_price_minor: 500,
    is_eligible: (ctx): boolean => ctx.plan_id === 'max',
  },
  extra_domain: {
    code: 'extra_domain',
    storage: 'quantity_row',
    yearly_price_range_minor: [1200, 2000],
    is_eligible: (): boolean => true,
  },
  // Pack référencement (plan MAX, pack ouvert, hors essai — le reste de l'éligibilité est
  // vérifié par le backend, `is_eligible` ne voit que le plan et la taille).
  seo_extra_backlink: {
    code: 'seo_extra_backlink',
    storage: 'quantity_row',
    monthly_price_minor: SEO_EXTRA_BACKLINK_MONTHLY_PRICE_MINOR,
    billing: 'next_month',
    checkout: false,
    max_quantity: SEO_EXTRA_BACKLINK_MAX_QUANTITY,
    is_eligible: (ctx): boolean => ctx.plan_id === 'max',
  },
  seo_articles_double: {
    code: 'seo_articles_double',
    storage: 'quantity_row',
    monthly_price_minor: SEO_ARTICLES_DOUBLE_MONTHLY_PRICE_MINOR,
    billing: 'next_month',
    checkout: false,
    max_quantity: 1,
    is_eligible: (ctx): boolean => ctx.plan_id === 'max',
  },
  white_label: {
    code: 'white_label',
    storage: 'flag',
    monthly_price_minor: 1500,
    is_eligible: (ctx): boolean => ctx.plan_id !== 'starter',
  },
  smart_ride: {
    code: 'smart_ride',
    storage: 'flag',
    monthly_price_minor: 3000,
    is_eligible: (): boolean => true,
  },
  mobile_app_whitelabel: {
    code: 'mobile_app_whitelabel',
    storage: 'flag',
    monthly_price_minor: 7900,
    is_eligible: (): boolean => true,
    available_after: 'packages/mobile shipped',
  },
};

// Top-ups one-shot — alimentent `pro_quota.credit_*_left` (reportables, pas de reset mensuel),
// ou, pour l'accélérateur référencement, une fenêtre de 3 mois à 3 liens (`seo_backlink_accelerator`).
export type TopupCode = 'sms_100' | 'emails_1000' | 'seo_backlink_accelerator';

export interface TopupDefinition {
  code: TopupCode;
  one_shot_price_minor: number;
  grants: { sms?: number; emails?: number; seo_backlinks?: number };
}

export const TOPUPS: Record<TopupCode, TopupDefinition> = {
  sms_100: { code: 'sms_100', one_shot_price_minor: 900, grants: { sms: 100 } },
  emails_1000: { code: 'emails_1000', one_shot_price_minor: 500, grants: { emails: 1_000 } },
  // 199 € HT payés à l'achat (facture one-shot), 3 liens / mois pendant 3 mois à partir du 1er du
  // mois suivant (U4, U9). Prix confirmé le 13/10/2026 : c'est l'offre d'appel.
  seo_backlink_accelerator: {
    code: 'seo_backlink_accelerator',
    one_shot_price_minor: SEO_BACKLINK_ACCELERATOR_PRICE_MINOR,
    grants: { seo_backlinks: SEO_ACCELERATOR_LINKS_PER_MONTH * SEO_ACCELERATOR_MONTHS },
  },
};

// =============================================================================
// Helpers
// =============================================================================

export function getPlanByEnum(value: Subcription): PlanDefinition {
  return PLANS[PLAN_ID_BY_ENUM[value]];
}

const SUBSCRIPTION_VALUES = new Set<string>(Object.values(Subcription));

/**
 * Convertit une valeur brute (string Prisma enum, JSON snapshot, etc.) en `Subcription` typé.
 * Throw si la valeur n'appartient pas à l'enum — protège contre des labels DB obsolètes
 * (ex: 'BOOSTER'/'ZEN' avant la migration de renommage).
 */
export function toSubscriptionEnum(value: string | null | undefined): Subcription {
  if (value && SUBSCRIPTION_VALUES.has(value)) {
    return value as Subcription;
  }
  throw new Error(`Invalid subscription label: ${String(value)}`);
}

export function getIncludedTeamSeats(plan: Subcription, is_team_plan: boolean): number {
  if (!is_team_plan) return 1;
  return getPlanByEnum(plan).prices.team.included_users;
}

export function getIncludedGmb(plan: Subcription): number {
  return getPlanByEnum(plan).quotas.gmb_connected;
}

export function getIncludedProEmailPerUser(plan: Subcription): number {
  return getPlanByEnum(plan).quotas.pro_email_per_user;
}

export function getMonthlyPulseMessages(plan: Subcription): number {
  const value = getPlanByEnum(plan).quotas.pulse_chat_per_user;
  return value === 'unlimited' ? PULSE_CHAT_MAX_SOFT_CAP_PER_USER : value;
}

export function isPulseUnlimited(plan: Subcription): boolean {
  return getPlanByEnum(plan).quotas.pulse_chat_per_user === 'unlimited';
}

export function getMonthlyPlanPrice(plan: Subcription, size: PlanSize, commitment: Commitment): number {
  const variant = getPlanByEnum(plan).prices[size];
  return commitment === 'committed_12m' ? variant.committed_minor : variant.monthly_minor;
}

/**
 * Applique la TVA à un montant en centimes HT et retourne le TTC arrondi.
 * Source de vérité partagée backend + frontend (montants `pro_subscription`,
 * segments, factures, prévisualisations) — évite la dérive entre `Math.round()`
 * et `+ vat * amount` qui peut produire des cents fantômes.
 */
export function applyVatToMinor(amount_tax_free_minor: number, vat_rate_percent: number): number {
  return Math.round(amount_tax_free_minor * (1 + vat_rate_percent / 100));
}

/**
 * Calcule le prorata du jour J pour un changement intra-segment (upgrade plan,
 * activation/augmentation d'addon).
 *
 * Formule : `round(delta × remainingMs / totalMs)`, clamp à 0 si delta ≤ 0
 * ou si le segment est dégénéré. Toutes les valeurs sont en centimes HT
 * (la TVA s'applique ensuite via `applyVatToMinor`).
 *
 * Source de vérité unique pour `previewPlanChange`, `confirmPlanChange`,
 * `previewChange` addon et `activate` addon — toute divergence ici =
 * écart entre le montant annoncé au client et le débit Stripe.
 */
export function computeProrataMinor(input: {
  delta_minor: number;
  segment_start: Date;
  segment_end: Date;
  now: Date;
}): number {
  if (input.delta_minor <= 0) return 0;
  // Calcul en jours pleins (et non en ms) pour éviter un montant qui varie à l'heure :
  // le ratio reste stable pendant toute la journée et change uniquement à minuit.
  // - totalDays : durée nominale du cycle (≈ 28-31 selon le mois), arrondie.
  // - remainingDays : jours restants jour-en-cours-inclus (ceil), clampés à totalDays.
  const MS_PER_DAY = 86_400_000;
  const totalDays = Math.max(1, Math.round((input.segment_end.getTime() - input.segment_start.getTime()) / MS_PER_DAY));
  const remainingDays = Math.min(
    totalDays,
    Math.max(0, Math.ceil((input.segment_end.getTime() - input.now.getTime()) / MS_PER_DAY)),
  );
  return Math.round(input.delta_minor * (remainingDays / totalDays));
}

export function planHasCapability(plan: Subcription, capability: string): boolean {
  return (getPlanByEnum(plan).capabilities as readonly string[]).includes(capability);
}

// =============================================================================
// Frais de rupture anticipée d'engagement (early break fee)
// =============================================================================

/**
 * Taux appliqué au solde restant dû pour rompre un engagement avant son terme.
 * 0.5 = le client paye 50% des mensualités restantes pour sortir immédiatement.
 *
 * Source de vérité : modifier ICI pour répercuter partout (preview, confirm,
 * affichage UI, comptabilité). Si plus tard on veut un taux par environnement,
 * exposer une env var côté backend et garder cette constante comme défaut.
 */
export const EARLY_BREAK_FEE_RATE = 0.5;

/**
 * Calcule l'indemnité forfaitaire pour rompre un engagement avant son terme.
 *
 * Formule : `monthly_price_minor × months_remaining × EARLY_BREAK_FEE_RATE`.
 *
 * `paid_until` représente la date jusqu'à laquelle l'abonnement est déjà facturé
 * (typiquement `current_segment.end`). On compte uniquement les cycles mensuels
 * QUI N'ONT PAS ENCORE ÉTÉ FACTURÉS — le mois en cours est déjà couvert par la
 * dernière mensualité, donc on ne le re-facture pas dans la break fee. Si pas de
 * segment courant disponible, le caller passe `now` (mode dégradé conservateur).
 *
 * Exemple : Pro YE engagé du 13/01/2026 au 13/01/2027, on est le 25/05/2026.
 *   - segment courant : 13/05 → 13/06 (déjà payé)
 *   - paid_until = 13/06/2026, commitment_end = 13/01/2027
 *   - months_remaining = 7 (juil → jan, juin déjà couvert)
 *
 * Retourne 0 si pas d'engagement actif (commitment_end null ou ≤ paid_until).
 *
 * Source de vérité unique pour `previewPlanChange`, `confirmPlanChange`,
 * `previewUnsub`, `handleUnsub` — toute divergence ici = écart entre le montant
 * annoncé au client et le débit Stripe.
 */
export function computeEarlyBreakFee(input: {
  monthly_price_minor: number;
  commitment_end: Date | null;
  paid_until: Date;
}): {
  amount_minor: number;
  months_remaining: number;
  rate: number;
} {
  if (!input.commitment_end || input.commitment_end.getTime() <= input.paid_until.getTime()) {
    return { amount_minor: 0, months_remaining: 0, rate: EARLY_BREAK_FEE_RATE };
  }
  // Normalise sur la date UTC (midnight) avant de compter les bornes mensuelles —
  // sinon le montant change selon l'heure dans la même journée.
  const paidDay = Date.UTC(
    input.paid_until.getUTCFullYear(),
    input.paid_until.getUTCMonth(),
    input.paid_until.getUTCDate(),
  );
  const endDay = Date.UTC(
    input.commitment_end.getUTCFullYear(),
    input.commitment_end.getUTCMonth(),
    input.commitment_end.getUTCDate(),
  );
  if (endDay <= paidDay) {
    return { amount_minor: 0, months_remaining: 0, rate: EARLY_BREAK_FEE_RATE };
  }
  // Compte les bornes mensuelles entre paidDay et endDay. Un mois partiel restant
  // (ex. 2 jours avant commitment_end) compte pour 1 mois plein — l'user paye une
  // tranche non proratisée, identique au comportement d'une mensualité.
  let monthsRemaining = 0;
  const cursor = new Date(paidDay);
  while (cursor.getTime() < endDay) {
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    monthsRemaining += 1;
  }
  const amountMinor = Math.round(input.monthly_price_minor * monthsRemaining * EARLY_BREAK_FEE_RATE);
  return { amount_minor: amountMinor, months_remaining: monthsRemaining, rate: EARLY_BREAK_FEE_RATE };
}

/**
 * Réponse standard pour exposer une break fee au frontend.
 * `amount` / `amount_tax_free` sont des chaînes pré-formatées par le backend
 * (transformToSchema applique `is_price`), `amount_minor` reste exposé pour les
 * calculs frontaux (UI conditionnelle "si fee > 0").
 */
export const EarlyBreakFeeSchema = Type.Object({
  amount: Type.String({ meta: { is_price: true, source_key: 'amount_minor' } }),
  amount_tax_free: Type.String({ meta: { is_price: true, source_key: 'amount_tax_free_minor' } }),
  amount_minor: Type.Number(),
  amount_tax_free_minor: Type.Number(),
  months_remaining: Type.Number(),
  rate: Type.Number(),
});
export type EarlyBreakFeeObject = Static<typeof EarlyBreakFeeSchema>;

// =============================================================================
// Activation / désactivation d'add-on — schémas API
// =============================================================================

export const ADDON_CODES_QUANTITY: readonly QuantityAddonCode[] = [
  'extra_user',
  'extra_gmb',
  'extra_domain',
  'seo_extra_backlink',
  'seo_articles_double',
];
export const ADDON_CODES_FLAG: readonly FlagAddonCode[] = ['white_label', 'smart_ride', 'mobile_app_whitelabel'];
export const ADDON_CODES_ALL: readonly AddonCode[] = [...ADDON_CODES_QUANTITY, ...ADDON_CODES_FLAG];
/**
 * Options du pack référencement (seo-upsell-offers.md) : proposées seulement aux comptes ouverts au
 * pack (flag JWT `is_seo_articles`) — le backend les refuse de toute façon sans pack actif.
 */
export const SEO_PACK_ADDON_CODES: readonly AddonCode[] = ['seo_extra_backlink', 'seo_articles_double'];
export const isSeoPackAddon = (code: AddonCode): boolean => SEO_PACK_ADDON_CODES.includes(code);

export const AddonActivationRequestSchema = Type.Object({
  quantity: Type.Optional(
    Type.Integer({
      minimum: 1,
      maximum: 100,
      title: 'INPUT_TITLE_ADDON_QUANTITY',
      placeholder: 'INPUT_PLACEHOLDER_ADDON_QUANTITY',
    }),
  ),
});
export type AddonActivationRequestObject = Static<typeof AddonActivationRequestSchema>;

export const ActiveAddonSchema = Type.Object({
  code: Type.String(),
  storage: Type.Union([Type.Literal('flag'), Type.Literal('quantity_row')]),
  quantity: Type.Integer(),
  monthly_price_minor: Type.Optional(Type.Integer()),
  // Set quand l'addon est en désactivation pending : le user a cliqué "désactiver" mais le
  // segment courant payé n'est pas encore terminé. Date pré-formatée par le backend
  // (cf. règle "Date Handling"). Absent = addon actif sans limite programmée.
  active_until_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'active_until', is_short: true } }),
  ),
  // Option programmée (facturation `next_month`) qui n'a pas encore démarré : date de début.
  // Absent = déjà en cours. Le backend ne le renseigne QUE quand `active_since` est à venir.
  active_since_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'active_since', is_short: true } }),
  ),
});

export const AddonActivationResponseSchema = Type.Object({
  active_addons: Type.Array(ActiveAddonSchema),
  next_renewal_at_formatted: Type.Optional(Type.String({ format: 'date-time' })),
  /**
   * `true` : le prorata part en prélèvement SEPA — ni payé ni impayé, facturé à l'encaissement
   * (quelques jours ouvrés). Absent : carte (débitée et facturée tout de suite) ou rien à débiter.
   */
  pending_debit: Type.Optional(Type.Boolean()),
});
export type AddonActivationResponseObject = Static<typeof AddonActivationResponseSchema>;

export const PreviewAddonRequestSchema = Type.Object({
  code: Type.String(),
  mode: Type.Union([Type.Literal('activate'), Type.Literal('modify'), Type.Literal('deactivate')]),
  quantity: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
});
export type PreviewAddonRequestObject = Static<typeof PreviewAddonRequestSchema>;

export const PreviewAddonResponseSchema = Type.Object({
  // Montant du jour J (TTC), 0 pour deactivate ou flag déjà actif.
  prorata_amount: Type.String({ meta: { is_price: true, source_key: 'prorata_amount_minor' } }),
  prorata_amount_tax_free: Type.String({ meta: { is_price: true, source_key: 'prorata_amount_tax_free_minor' } }),
  prorata_amount_minor: Type.Number(),
  // Remise déjà déduite du prorata ci-dessus (codes ciblant les options + enveloppe budgétaire), HT.
  prorata_discount_tax_free: Type.String({ meta: { is_price: true, source_key: 'prorata_discount_tax_free_minor' } }),
  prorata_discount_tax_free_minor: Type.Number(),
  // Nouveau total mensuel applicable à partir de la date d'effet (TTC).
  new_monthly_amount: Type.String({ meta: { is_price: true, source_key: 'new_monthly_amount_minor' } }),
  new_monthly_amount_tax_free: Type.String({
    meta: { is_price: true, source_key: 'new_monthly_amount_tax_free_minor' },
  }),
  // Date d'effet (1er du mois suivant pour les modifications). Pré-formatée par le backend.
  effective_date_string: Type.String({
    format: 'date-time',
    meta: { source_key: 'effective_date', is_short: true },
  }),
  // True si le prorata > 0 et que l'utilisateur n'a pas de moyen de paiement par défaut.
  requires_payment_method: Type.Boolean(),
  // Mode de facturation de l'option (cf. `AddonBilling`). `next_month` : prorata toujours à 0,
  // `effective_date` = 1er du mois suivant, et `next_invoice_date_string` = échéance qui portera
  // la première ligne au prix plein (peut précéder ou suivre la date d'effet d'au plus un mois).
  billing: Type.Union([Type.Literal('prorata'), Type.Literal('next_month')]),
  next_invoice_date_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'next_invoice_date', is_short: true } }),
  ),
});
export type PreviewAddonResponseObject = Static<typeof PreviewAddonResponseSchema>;

// =============================================================================
// computeMonthlyAmount — fonction pure, source de vérité du montant à débiter
// =============================================================================
// Utilisée par le cron de renouvellement (paiement) ET l'émission de facture.
// Ne couvre PAS les top-ups (one-shot) ni `extra_domain` (annuel).
// =============================================================================

export interface ActiveAddon {
  code: AddonCode;
  quantity: number;
}

export interface MonthlyAmountBreakdownLine {
  label: string;
  code: 'plan' | AddonCode;
  amount_tax_free_minor: number;
}

export interface MonthlyAmountResult {
  amount_tax_free_minor: number;
  amount_minor: number; // TTC
  vat_amount_minor: number;
  breakdown: MonthlyAmountBreakdownLine[];
}

export function computeMonthlyAmount(input: {
  plan: Subcription;
  size: PlanSize;
  commitment: Commitment;
  active_addons: ActiveAddon[];
  vat_rate_percent: number;
}): MonthlyAmountResult {
  const breakdown: MonthlyAmountBreakdownLine[] = [];

  const planDef = getPlanByEnum(input.plan);
  const planPrice = getMonthlyPlanPrice(input.plan, input.size, input.commitment);
  breakdown.push({
    code: 'plan',
    label: `${planDef.label} ${input.size === 'team' ? 'Team' : 'Solo'} ${input.commitment === 'committed_12m' ? 'engagé' : 'libre'}`,
    amount_tax_free_minor: planPrice,
  });

  for (const addon of input.active_addons) {
    const def = ADDONS[addon.code];
    // Skip yearly add-ons (extra_domain) — facturés à part annuellement
    if (def.monthly_price_minor === undefined) continue;
    const lineAmount = def.monthly_price_minor * addon.quantity;
    // `label` est volontairement la `code` brute (clé de traduction `ADDON_LABEL_*`).
    // Le formatage final (libellé + quantité) est fait par le consommateur côté UI/email
    // pour respecter la locale du pro et la règle "pas de texte hardcodé" (CLAUDE.md §7).
    breakdown.push({
      code: addon.code,
      label: addon.code,
      amount_tax_free_minor: lineAmount,
    });
  }

  const amount_tax_free_minor = breakdown.reduce(
    (sum: number, line: MonthlyAmountBreakdownLine): number => sum + line.amount_tax_free_minor,
    0,
  );
  const amount_minor = applyVatToMinor(amount_tax_free_minor, input.vat_rate_percent);
  const vat_amount_minor = amount_minor - amount_tax_free_minor;

  return { amount_tax_free_minor, amount_minor, vat_amount_minor, breakdown };
}

type SubscriptionFeatureMap = {
  [feature_key: string]: {
    title?: string;
    values: Partial<Record<SubscriptionKey, boolean>>;
  };
};

export const SUBSCRIPTION_FEATURES: SubscriptionFeatureMap = {
  FEATURE_ONLINE_BOOKING: {
    title: 'ONLINE_BOOKING_SECTION_TITLE',
    values: {
      STARTER: true,
      PRO: true,
      MAX: true,
    },
  },
  FEATURE_ONLINE_BOOKING_WIDGET: {
    values: {
      PRO: true,
      MAX: true,
    },
  },
  FEATURE_GOOGLE_BOOK_REDIRECT_BUTTON: {
    values: {
      MAX: true,
    },
  },
  FEATURE_BOOKING_SMS_REMIND: {
    values: {
      MAX: true,
    },
  },
  FEATURE_WEBSITE_AND_DOMAIN: {
    title: 'ATTRACT_SECTION_TITLE',
    values: {
      MAX: true,
    },
  },
  FEATURE_SEO: {
    values: {
      MAX: true,
    },
  },
  FEATURE_SYNC_GBM: {
    values: {
      MAX: true,
    },
  },
  FEATURE_REVIEW_COLLECT: {
    values: {
      MAX: true,
    },
  },
  FEATURE_CRM_CUSTOMER: {
    title: 'CUSTOMER_SECTION_TITLE',
    values: {
      MAX: true,
    },
  },
  FEATURE_CUSTOMER_STATS: {
    values: {
      MAX: true,
    },
  },
  FEATURE_SEGMENT_CUSTOMER: {
    title: 'CUSTOMER_LOYALTY_SECTION_TITLE',
    values: {
      MAX: true,
    },
  },
  FEATURE_CAMPAIGN_AND_NEWS: {
    values: {
      MAX: true,
    },
  },
  FEATURE_MEASURE_CAMPAIGN_IMPACT: {
    values: {
      MAX: true,
    },
  },
  FEATURE_PAYMENT_TOTALLY_OR_DEPOSIT: {
    title: 'PAYMENT_SECTION_TITLE',
    values: {
      MAX: true,
    },
  },
  FEATURE_CUSTOMER_PAYMENT_LINK: {
    values: {
      MAX: true,
    },
  },
  FEATURE_IMPRINT: {
    values: {
      MAX: true,
    },
  },
  FEATURE_ONLINE_TUTORIEL: {
    title: 'SUPPORT_SECTION_TITLE',
    values: {
      MAX: true,
    },
  },
  FEATURE_LIVE_CHAT: {
    values: {
      MAX: true,
    },
  },
  FEATURE_VISIO_SUPPORT: {
    values: {
      MAX: true,
    },
  },
};

export enum BillingPeriod {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

/**
 * Listing public des plans (marketing pricing).
 *
 * NOTE: les champs `*_price_string` / `annual_saving_amount_string` sont déjà formatés côté
 * backend dans `pro_subscription.service.getListSubcriptionPrices()` via `ParsePrice()`
 * (pas via `transformToSchema()` + `meta.is_price`). Le frontend les affiche tels quels.
 * Si tu ajoutes un champ raw `*_minor` un jour, passe-le par `transformToSchema()` avec
 * `meta: { is_price: true, source_key: '...' }` plutôt que de re-formater manuellement.
 */
export const SubscriptionPlanSchema = Type.Object({
  label: Type.Enum(Subcription),
  monthly_price_string: Type.String(),
  yearly_price_string: Type.String(),
});

export const SubscriptionPlanListSchema = Type.Object({
  list_prices: Type.Array(SubscriptionPlanSchema),
  annual_saving_percentage: Type.Number(),
  annual_saving_amount_string: Type.String(),
});
export type SubscriptionPlanListSchemaObject = Static<typeof SubscriptionPlanListSchema>;

/**
 * Options proposées AU MOMENT de souscrire (première activation payante, page « Choisir ce
 * forfait »). Source unique front + back : le front les affiche, le back refuse tout code hors
 * de cette liste. Sont exclues :
 *  - les options facturées annuellement (`extra_domain`, prix dépendant du domaine choisi) ;
 *  - les options non encore livrées (`available_after`) ;
 *  - les options réservées à un compte déjà actif (`checkout: false`, ex. pack référencement — U15) ;
 *  - les options non éligibles au couple (plan, taille) choisi.
 */
export function listSubscriptionCheckoutAddons(ctx: { plan_id: PlanId; size: PlanSize }): AddonDefinition[] {
  return ADDON_CODES_ALL.map((code): AddonDefinition => ADDONS[code]).filter(
    (def): boolean =>
      def.monthly_price_minor !== undefined &&
      def.available_after === undefined &&
      def.checkout !== false &&
      def.is_eligible(ctx),
  );
}

export const SubscriptionCheckoutAddonSchema = Type.Object({
  code: Type.Union(ADDON_CODES_ALL.map((code): TLiteral<AddonCode> => Type.Literal(code))),
  // Seules les options « quantité » (extra_user, extra_gmb) lisent ce champ ; ignoré pour un flag.
  quantity: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
});
export type SubscriptionCheckoutAddonObject = Static<typeof SubscriptionCheckoutAddonSchema>;

export const GetSubscriptionLinkRequestSchema = Type.Object({
  subscription_label: Type.Enum(Subcription),
  billing_period: Type.Enum(BillingPeriod),
  // Taille de la formule (Solo par défaut — compat avec les anciens clients qui ne l'envoient pas).
  size: Type.Optional(Type.Union([Type.Literal('solo'), Type.Literal('team')])),
  // Options choisies à la souscription (cf. listSubscriptionCheckoutAddons). Uniquement pour une
  // PREMIÈRE activation : un abonné gère ses options depuis « Mes options ».
  addons: Type.Optional(Type.Array(SubscriptionCheckoutAddonSchema, { maxItems: 10 })),
});
export type GetSubscriptionLinkRequestObject = Static<typeof GetSubscriptionLinkRequestSchema>;

export const PreviewPlanChangeRequestSchema = Type.Object({
  subscription_label: Type.Enum(Subcription),
  billing_period: Type.Enum(BillingPeriod),
  size: Type.Union([Type.Literal('solo'), Type.Literal('team')]),
});
export type PreviewPlanChangeRequestObject = Static<typeof PreviewPlanChangeRequestSchema>;

// Dépassement de limite projeté sur le plan cible (cf. `getLimitOveragesForTarget`).
// Inline ici plutôt que dans subscription-limits.ts pour éviter une dépendance
// circulaire (subscription-limits importe déjà depuis ce fichier).
export const LimitOverageSchema = Type.Object({
  resource: Type.Union([Type.Literal('TEAM_MEMBER'), Type.Literal('GMB'), Type.Literal('MAILBOX')]),
  used: Type.Integer(),
  target_limit: Type.Integer(),
});
export type LimitOverageObject = Static<typeof LimitOverageSchema>;

// Remise effectivement appliquée sur la part plan d'un tarif mensuel (code promo actif du pro).
export const PlanChangeAppliedPromoSchema = Type.Object({
  code: Type.String(),
  label: Type.String(),
  amount_off: Type.String({ meta: { is_price: true, source_key: 'amount_off_minor' } }),
  amount_off_tax_free: Type.String({ meta: { is_price: true, source_key: 'amount_off_tax_free_minor' } }),
});
export type PlanChangeAppliedPromoObject = Static<typeof PlanChangeAppliedPromoSchema>;

export const PreviewPlanChangeResponseSchema = Type.Object({
  is_same: Type.Boolean(),
  is_upgrade: Type.Boolean(),
  is_downgrade: Type.Boolean(),
  // True quand la bascule est différée (downgrade, ou transition YEARLY engagé → MONTHLY,
  // ou changement de billing_period same-rank). False quand l'effet est immédiat (upgrade
  // hors engagement). Utilisé par le frontend pour choisir le bon disclaimer.
  is_deferred: Type.Boolean(),
  // Tarifs mensuels (part plan, hors addons) RÉELLEMENT facturés : catalogue moins les codes
  // promo actifs du pro — pour la cible, hors codes que ce changement annule (`voided_promos`).
  // Même moteur que la facturation (`applyPromos`), donc même éligibilité et même plancher.
  current_amount: Type.String({ meta: { is_price: true, source_key: 'current_amount_minor' } }),
  current_amount_tax_free: Type.String({ meta: { is_price: true, source_key: 'current_amount_tax_free_minor' } }),
  target_amount: Type.String({ meta: { is_price: true, source_key: 'target_amount_minor' } }),
  target_amount_tax_free: Type.String({ meta: { is_price: true, source_key: 'target_amount_tax_free_minor' } }),
  // Tarifs catalogue avant remise — à barrer dans l'UI quand `*_applied_promos` n'est pas vide.
  current_amount_catalog: Type.String({ meta: { is_price: true, source_key: 'current_amount_catalog_minor' } }),
  current_amount_catalog_tax_free: Type.String({
    meta: { is_price: true, source_key: 'current_amount_catalog_tax_free_minor' },
  }),
  target_amount_catalog: Type.String({ meta: { is_price: true, source_key: 'target_amount_catalog_minor' } }),
  target_amount_catalog_tax_free: Type.String({
    meta: { is_price: true, source_key: 'target_amount_catalog_tax_free_minor' },
  }),
  // Codes promo appliqués sur chaque tarif (vides si aucune remise ne s'applique).
  current_applied_promos: Type.Array(PlanChangeAppliedPromoSchema),
  target_applied_promos: Type.Array(PlanChangeAppliedPromoSchema),
  prorata_amount: Type.String({ meta: { is_price: true, source_key: 'prorata_amount_minor' } }),
  prorata_amount_tax_free: Type.String({ meta: { is_price: true, source_key: 'prorata_amount_tax_free_minor' } }),
  prorata_amount_minor: Type.Number(),
  effective_date_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'effective_date', is_short: true } }),
  ),
  next_renewal_date_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'next_renewal_date', is_short: true } }),
  ),
  // Durée d'engagement du target (null si target = MONTHLY).
  target_commitment_months: Type.Union([Type.Number(), Type.Null()]),
  // Date de fin du nouvel engagement (effective_date + commitment_months) si target = YEARLY.
  target_commitment_end_date_string: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { source_key: 'target_commitment_end_date', is_short: true },
    }),
  ),
  // Codes promo qui seront voided par ce changement (vide si aucun). Frontend doit
  // afficher un bandeau d'avertissement avant que l'utilisateur confirme.
  voided_promos: Type.Array(
    Type.Object({
      code: Type.String(),
      label: Type.String(),
    }),
  ),
  // Indemnité de rupture anticipée d'engagement. Présente UNIQUEMENT quand
  // `is_deferred=true` ET qu'un engagement est en cours (sinon `undefined`).
  // Permet au frontend d'offrir un opt-in "rompre maintenant pour X€" en
  // alternative au changement programmé. Montants TTC + HT pré-formatés.
  early_break_fee: Type.Optional(EarlyBreakFeeSchema),
  // Ressources existantes qui dépasseraient la limite du plan cible (after
  // addon eligibility check). Vide si aucun dépassement. `confirmPlanChange`
  // refuse la requête tant que ce tableau n'est pas vide — frontend doit
  // afficher un blocage avec liste des ressources concernées.
  limit_overages: Type.Array(LimitOverageSchema),
  // Options actives qui ne sont pas éligibles au plan cible : `confirmPlanChange` les désactive
  // automatiquement (effet en fin de période déjà facturée). Vide si aucune.
  deactivated_addons: Type.Array(
    Type.Object({
      code: Type.String(),
      quantity: Type.Integer(),
    }),
  ),
});
export type PreviewPlanChangeResponseObject = Static<typeof PreviewPlanChangeResponseSchema>;

export const ConfirmPlanChangeRequestSchema = Type.Object({
  subscription_label: Type.Enum(Subcription),
  billing_period: Type.Enum(BillingPeriod),
  size: Type.Union([Type.Literal('solo'), Type.Literal('team')]),
  // Opt-in pour rompre l'engagement actuel avec une indemnité (50% du solde) et
  // appliquer le changement immédiatement, au lieu d'attendre la fin d'engagement.
  // Ignoré si pas d'engagement actif ou si le changement n'est pas différé.
  with_early_break: Type.Optional(Type.Boolean()),
});
export type ConfirmPlanChangeRequestObject = Static<typeof ConfirmPlanChangeRequestSchema>;

export const ConfirmPlanChangeResponseSchema = Type.Object({
  applied_immediately: Type.Boolean(),
  effective_date_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'effective_date', is_short: true } }),
  ),
  /**
   * `true` : le prorata et/ou les frais de rupture partent en prélèvement SEPA — ni payés ni
   * impayés, facture du prorata à l'encaissement. Absent : carte ou rien à débiter.
   */
  pending_debit: Type.Optional(Type.Boolean()),
});
export type ConfirmPlanChangeResponseObject = Static<typeof ConfirmPlanChangeResponseSchema>;

export const UnsubscribeRequestSchema = Type.Object({
  unsubscribe_reason: Type.Optional(
    Type.String({
      placeholder: 'INPUT_PLACEHOLDER_UNSUB_REASON',
      title: 'INPUT_TITLE_UNSUB_REASON',
      maxLength: 2000,
      meta: {
        suggestions: Object.values(UnsubscribeReason),
        disable_search: false,
        allow_free_text: true,
        multiple: false,
      },
    }),
  ),
  feedback: Type.Optional(
    Type.String({
      meta: { multiline: true },
      placeholder: 'INPUT_PLACEHOLDER_UNSUB_FEEDBACK',
      title: 'INPUT_TITLE_UNSUB_FEEDBACK',
      maxLength: 2000,
    }),
  ),
});
export type GetUnsubscribeRequestObject = Static<typeof UnsubscribeRequestSchema>;

export const UnsubscribeResponseSchema = Type.Object({
  offer: Type.Optional(Type.Enum(UnsubscribeOfferReason)),
  offer_expiration: Type.String(),
  offer_expiration_string: Type.String({ format: 'date-time', meta: { is_short: true, with_time: true } }),
  // Date à laquelle la résiliation prendra effet par défaut (gratuit) si l'utilisateur
  // confirme sans break fee. Pré-formatée par le backend.
  default_effective_end_date_string: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { source_key: 'default_effective_end_date', is_short: true },
    }),
  ),
  // Indemnité de rupture exposée UNIQUEMENT si un engagement est en cours. Permet
  // au frontend d'offrir un opt-in "résilier maintenant pour X€".
  early_break_fee: Type.Optional(EarlyBreakFeeSchema),
});
export type UnsubscribeResponseObject = Static<typeof UnsubscribeResponseSchema>;

export const HandleUnsubRequestSchema = Type.Object({
  // Opt-in pour solder l'engagement maintenant via break fee et terminer l'abo
  // immédiatement. Ignoré si pas d'engagement actif.
  with_early_break: Type.Optional(Type.Boolean()),
});
export type HandleUnsubRequestObject = Static<typeof HandleUnsubRequestSchema>;

export const UnsubConfirmResponseSchema = Type.Object({
  effective_end_date: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'subscription_end_date', is_short: true } }),
  ),
  /**
   * `true` : les frais de rupture partent en prélèvement SEPA (encaissés sous quelques jours
   * ouvrés) ; la résiliation, elle, est immédiate. Absent : carte ou résiliation sans frais.
   */
  pending_debit: Type.Optional(Type.Boolean()),
});
export type UnsubConfirmResponseObject = Static<typeof UnsubConfirmResponseSchema>;

// =============================================================================
// Upcoming invoices — lecture des prochains segments
// =============================================================================

export const UpcomingInvoiceLineItemSchema = Type.Object({
  kind: Type.Union([Type.Literal('plan'), Type.Literal('addon'), Type.Literal('promo')]),
  code: Type.String(),
  label: Type.String(),
  amount: Type.String({ meta: { is_price: true, source_key: 'amount' } }),
  amount_tax_free: Type.String({ meta: { is_price: true, source_key: 'amount_tax_free' } }),
  quantity: Type.Integer(),
});

export const UpcomingInvoiceSchema = Type.Object({
  start: Type.String({ format: 'date-time' }),
  end: Type.String({ format: 'date-time' }),
  start_string: Type.String({ format: 'date-time', meta: { source_key: 'start', is_short: true } }),
  end_string: Type.String({ format: 'date-time', meta: { source_key: 'end', is_short: true } }),
  amount: Type.String({ meta: { is_price: true, source_key: 'amount_with_discount' } }),
  amount_tax_free: Type.String({ meta: { is_price: true, source_key: 'amount_with_discount_tax_free' } }),
  is_trial: Type.Boolean(),
  is_cancelable: Type.Boolean(),
  line_items: Type.Array(UpcomingInvoiceLineItemSchema),
});

export const UpcomingInvoicesResponseSchema = Type.Object({
  items: Type.Array(UpcomingInvoiceSchema),
  subscription_end_date: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'subscription_end_date', is_short: true } }),
  ),
});
export type UpcomingInvoicesResponseObject = Static<typeof UpcomingInvoicesResponseSchema>;

// =============================================================================
// Past invoices — factures émises (lues depuis billing_event + Stripe pour PDF)
// =============================================================================

export const PastInvoiceSchema = Type.Object({
  /**
   * ⚠️ Id de la facture CHEZ LE PROVIDER (`in_…` Stripe, id numérique Pennylane), historiquement
   * exposé sous ce nom. La clé primaire interne est `internal_invoice_id` — c'est elle qu'il faut
   * passer à l'endpoint de résolution du PDF.
   */
  invoice_id: Type.String(),
  /** PK interne `invoice.invoice_id` — clé de `GET /app/pro-subscription/invoices/:id/pdf-url`. */
  internal_invoice_id: Type.Number(),
  period_year_month: Type.String(),
  amount: Type.String({ meta: { is_price: true, source_key: 'amount_minor' } }),
  issued_at_string: Type.String({
    format: 'date-time',
    meta: { source_key: 'done_at', is_short: true },
  }),
  status: Type.String(),
  hosted_url: Type.Optional(Type.String()),
  /**
   * ⚠️ URL persistée à l'émission — elle peut être PÉRIMÉE (les liens publics Pennylane portent
   * une expiration dans leur jeton chiffré et finissent par renvoyer 400). Ne sert plus qu'à
   * savoir s'il existe un PDF : pour l'ouvrir, passer par l'endpoint `…/pdf-url` qui en résout
   * un frais auprès du provider.
   */
  pdf_url: Type.Optional(Type.String()),
});
export type PastInvoiceObject = Static<typeof PastInvoiceSchema>;

export const PastInvoicesResponseSchema = Type.Object({
  items: Type.Array(PastInvoiceSchema),
});
export type PastInvoicesResponseObject = Static<typeof PastInvoicesResponseSchema>;

/**
 * Lien de téléchargement du PDF d'une facture, résolu à la demande.
 *
 * Pourquoi un endpoint plutôt que l'URL stockée : chez Pennylane, `public_file_url` embarque un
 * jeton chiffré à durée de vie limitée. L'URL enregistrée en base au moment de l'émission cesse
 * donc de fonctionner (400) au bout de quelques jours. On la redemande au provider au moment du
 * clic. Partagé entre l'app pro et l'admin.
 */
export const InvoicePdfUrlResponseSchema = Type.Object({
  pdf_url: Type.String(),
});
export type InvoicePdfUrlResponseObject = Static<typeof InvoicePdfUrlResponseSchema>;

// =============================================================================
// Subscription summary — état courant de l'abo pour l'UI
// =============================================================================

export const PendingPlanChangeSchema = Type.Object({
  current_plan_label: Type.Enum(Subcription),
  target_plan_label: Type.Enum(Subcription),
  target_amount: Type.String({ meta: { is_price: true, source_key: 'target_amount_minor' } }),
  target_amount_tax_free: Type.String({ meta: { is_price: true, source_key: 'target_amount_tax_free_minor' } }),
  effective_at_string: Type.String({
    format: 'date-time',
    meta: { source_key: 'effective_at', is_short: true },
  }),
});

// =============================================================================
// pending_change_revert — snapshot pré-downgrade stocké en JSON sur pro_subscription.
// Source de vérité partagée pour le shape + un builder qui garantit la cohérence
// entre l'écriture (confirmPlanChange) et la lecture (getSubscriptionSummary,
// cancelPendingPlanChange).
// =============================================================================

export type PrismaPlanSize = 'SOLO' | 'TEAM';
export type PrismaCommitment = 'MONTHLY' | 'COMMITTED_12M';

/**
 * Source de vérité partagée pour les littéraux `size`/`commitment` (miroir des enums Prisma).
 * Réutilisée par les schémas TypeBox (snapshot revert, override admin) et les dropdowns admin-web
 * pour éviter de redéclarer les mêmes unions/arrays à plusieurs endroits.
 */
export const PlanSizeSchema = Type.Union([Type.Literal('SOLO'), Type.Literal('TEAM')]);
export const PlanCommitmentSchema = Type.Union([Type.Literal('MONTHLY'), Type.Literal('COMMITTED_12M')]);
export const PLAN_SIZE_VALUES: readonly PrismaPlanSize[] = ['SOLO', 'TEAM'];
export const PLAN_COMMITMENT_VALUES: readonly PrismaCommitment[] = ['MONTHLY', 'COMMITTED_12M'];

/**
 * Schéma TypeBox du snapshot — utilisé pour valider en runtime ce qu'on lit en DB
 * (cf. `parsePendingChangeRevert`). Un JSON mal formé ou venant d'une version
 * antérieure du shape doit être traité comme "pas de revert" et non casser le service.
 */
export const PendingChangeRevertSchema = Type.Object({
  subscription_label: Type.Enum(Subcription),
  size: PlanSizeSchema,
  billing_period: Type.Enum(BillingPeriod),
  commitment: PlanCommitmentSchema,
  commitment_months: Type.Union([Type.Number(), Type.Null()]),
  subscription_commitment_end_date: Type.Union([Type.String(), Type.Null()]),
  amount: Type.Number(),
  amount_tax_free: Type.Number(),
  effective_at: Type.String(),
  // Options inéligibles au plan cible, coupées à `effective_at` par le changement différé : à
  // remettre si le pro annule. `rows` = lignes `pro_subscription_addon` touchées (fin d'origine,
  // ou ligne retirée car elle ne démarrait qu'après le changement). Absent sur les anciens snapshots.
  dropped_addons: Type.Optional(
    Type.Array(
      Type.Object({
        code: Type.String(),
        quantity: Type.Number(),
        rows: Type.Array(
          Type.Object({
            pro_subscription_addon_id: Type.Number(),
            previous_active_until: Type.Union([Type.String(), Type.Null()]),
            removed: Type.Boolean(),
          }),
        ),
      }),
    ),
  ),
});
export type PendingChangeRevert = Static<typeof PendingChangeRevertSchema>;
export type PendingChangeDroppedAddon = NonNullable<PendingChangeRevert['dropped_addons']>[number];

/**
 * Lit un `pending_change_revert` JSON arbitraire et retourne un PendingChangeRevert
 * validé OU null si le shape est inattendu. Évite les `as PendingChangeRevert` aveugles
 * côté service qui propageraient des `undefined` sur les champs renommés/supprimés.
 */
export function parsePendingChangeRevert(json: unknown): PendingChangeRevert | null {
  if (json === null || json === undefined) return null;
  if (!Value.Check(PendingChangeRevertSchema, json)) return null;
  return json;
}

export interface RevertSnapshotSource {
  subscription_label: string;
  size: string;
  billing_period: string;
  commitment: string;
  commitment_months: number | null;
  subscription_commitment_end_date: Date | string | null;
  amount: number;
  amount_tax_free: number;
}

export function buildPendingChangeRevert(sub: RevertSnapshotSource, effective_at: Date | string): PendingChangeRevert {
  return {
    subscription_label: sub.subscription_label as Subcription,
    size: sub.size as PrismaPlanSize,
    billing_period: sub.billing_period as BillingPeriod,
    commitment: sub.commitment as PrismaCommitment,
    commitment_months: sub.commitment_months,
    subscription_commitment_end_date:
      sub.subscription_commitment_end_date instanceof Date
        ? sub.subscription_commitment_end_date.toISOString()
        : sub.subscription_commitment_end_date,
    amount: sub.amount,
    amount_tax_free: sub.amount_tax_free,
    effective_at: effective_at instanceof Date ? effective_at.toISOString() : effective_at,
  };
}

/**
 * Ajoute au snapshot les options coupées par un changement différé. Une option déjà présente garde
 * son entrée d'origine (downgrade après downgrade : c'est l'état d'avant le 1er qu'il faut remettre).
 */
export function withDroppedAddons(
  revert: PendingChangeRevert,
  dropped: PendingChangeDroppedAddon[],
): PendingChangeRevert {
  const existing = revert.dropped_addons ?? [];
  const known = new Set(existing.map((a): string => a.code));
  const merged = [...existing, ...dropped.filter((a): boolean => !known.has(a.code))];
  return merged.length > 0 ? { ...revert, dropped_addons: merged } : revert;
}

export function isPendingChangeRevertStillActive(revert: PendingChangeRevert, now: Date): boolean {
  return new Date(revert.effective_at) > now;
}

/**
 * Prélèvement SEPA (GoCardless) EN COURS le plus récent du pro — ni payé ni impayé : bandeau
 * « prélèvement en cours » de la page abonnement. Montant et date de lancement formatés par le backend
 * (Price / Date Handling Rules).
 */
export const PendingSepaDebitSchema = Type.Object({
  amount: Type.String({ meta: { is_price: true, source_key: 'amount_minor' } }),
  launched_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'launched_at' } }),
});
export type PendingSepaDebitObject = Static<typeof PendingSepaDebitSchema>;

export const SubscriptionSummaryResponseSchema = Type.Object({
  plan_id: Type.String(),
  plan_label: Type.String(),
  size: Type.String(),
  commitment: Type.String(),
  status: Type.String(),
  amount: Type.String({ meta: { is_price: true, source_key: 'amount' } }),
  amount_tax_free: Type.String({ meta: { is_price: true, source_key: 'amount_tax_free' } }),
  next_renewal_at: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'next_renewal_at', is_short: true } }),
  ),
  subscription_end_date: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'subscription_end_date', is_short: true } }),
  ),
  subscription_commitment_end_date_string: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { source_key: 'subscription_commitment_end_date', is_short: true },
    }),
  ),
  active_addons: Type.Array(ActiveAddonSchema),
  can_cancel_now: Type.Boolean(),
  pending_plan_change: Type.Optional(PendingPlanChangeSchema),
  /**
   * Cycle de dunning en cours (impayé non résolu). `dunning_phase` = RECOVERY (J0→J+30,
   * retries en cours) ou SEMI_FROZEN (J+30→J+60, résa en ligne publique coupée — pilote la
   * modale bloquante à la connexion). Absents quand l'abo est sain. Le solde impayé est
   * pré-formaté backend (Price Handling Rule).
   */
  dunning_phase: Type.Optional(Type.String()),
  outstanding_amount: Type.Optional(Type.String()),
  /**
   * Prélèvement SEPA en cours le plus récent (débit de facturation lancé, ou page de paiement
   * PROCESSING), `null` s'il n'y en a pas. Toujours renvoyé par le backend ; optionnel dans le type pour
   * les clients générés avant ce champ.
   */
  pending_sepa_debit: Type.Optional(Type.Union([PendingSepaDebitSchema, Type.Null()])),
});
export type SubscriptionSummaryResponseObject = Static<typeof SubscriptionSummaryResponseSchema>;

/**
 * Réponse des rejeux self-service d'un impayé d'abonnement (« Payer maintenant », régularisation
 * d'un compte gelé). Un prélèvement SEPA lancé n'est ni payé ni refusé : tant qu'il est en cours
 * (`pending_debits` > 0), l'impayé n'est pas soldé et un compte gelé ne se réactive qu'à
 * l'encaissement, 2 à 5 jours ouvrés plus tard (`recovered` reste false). Une carte solde
 * immédiatement.
 */
export const SubscriptionRegularizationResponseSchema = Type.Object({
  message: Type.String(),
  /** true : plus rien n'est dû ni en cours — compte à jour (réactivé tout de suite s'il était gelé). */
  recovered: Type.Optional(Type.Boolean()),
  /** Nombre de prélèvements SEPA du pro en cours de règlement. */
  pending_debits: Type.Optional(Type.Number()),
});
export type SubscriptionRegularizationResponseObject = Static<typeof SubscriptionRegularizationResponseSchema>;

export const ReloadRequestSchema = Type.Object({
  product: Type.Enum(ReloadProduct),
  current_page: Type.String(),
});
export type ReloadRequestObject = Static<typeof ReloadRequestSchema>;

//DONT TOUCH below this line
validateEnumTranslations(UnsubscribeReason, UnsubscribeReasonTranslation, '', true);
